import type { Route } from "next";

export interface ContactChannelContent {
  title: string;
  email: string;
  usageGuidance: string;
  responseExpectation: string;
}

export interface ContactHelpfulLinkContent {
  label: string;
  url: Route;
}

export const CONTACT_PAGE_CONTENT = {
  hero: {
    heading: "Get In Touch",
    subheading:
      "We're here to help. Whether you need support with an order, want to become a supplier, or have a question about our platform, our team is ready to assist.",
  },
  channels: [
    {
      title: "General Enquiries",
      email: "hello@wmart.co.uk",
      usageGuidance: "General questions and platform enquiries.",
      responseExpectation: "Response within 1-2 business days.",
    },
    {
      title: "Orders & Delivery",
      email: "orders@wmart.co.uk",
      usageGuidance: "Order and delivery queries. Users should include their order number.",
      responseExpectation: "1 business day.",
    },
    {
      title: "Returns & Refunds",
      email: "returns@wmart.co.uk",
      usageGuidance: "Return and refund requests. Users should include order number and reason.",
      responseExpectation: "2 business days.",
    },
    {
      title: "Complaints",
      email: "complaints@wmart.co.uk",
      usageGuidance: "Formal complaints.",
      responseExpectation:
        "Aim to respond within 5 business days, with acknowledgement within 1 day.",
    },
    {
      title: "Accounts & Billing",
      email: "accounts@wmart.co.uk",
      usageGuidance: "Invoice queries, payment issues, and account billing matters.",
      responseExpectation: "2 business days.",
    },
    {
      title: "Supplier & Trade Enquiries",
      email: "trade@wmart.co.uk",
      usageGuidance:
        "New supplier applications, existing supplier queries, and trade account requests.",
      responseExpectation: "Supplier applications: 5-7 business days.",
    },
    {
      title: "Partnerships & Business Development",
      email: "partnerships@wmart.co.uk",
      usageGuidance:
        "Strategic partnerships, co-marketing, and joint business opportunities.",
      responseExpectation: "3-5 business days.",
    },
    {
      title: "Media & Press",
      email: "press@wmart.co.uk",
      usageGuidance: "Journalist enquiries, press releases, and media requests.",
      responseExpectation: "1-2 business days.",
    },
    {
      title: "Privacy & Data Rights",
      email: "privacy@wmart.co.uk",
      usageGuidance:
        "Data subject access requests, data deletion requests, and UK GDPR-related matters.",
      responseExpectation: "Within 1 calendar month for DSARs.",
    },
    {
      title: "Careers",
      email: "careers@wmart.co.uk",
      usageGuidance: "CV submissions and enquiries about current or future roles.",
      responseExpectation: "General acknowledgement only; no explicit SLA committed in V1.",
    },
    {
      title: "Legal & Compliance",
      email: "legal@wmart.co.uk",
      usageGuidance: "Legal notices, compliance queries, and contract matters.",
      responseExpectation: "General acknowledgement only; no explicit SLA committed in V1.",
    },
  ] satisfies ContactChannelContent[],
  telephone: {
    label: "Telephone",
    number: "[Phone Number]",
    serviceHours: "Monday to Friday, 9:00am - 5:30pm. Closed on bank holidays.",
  },
  registeredAddress: {
    companyName: "[Company Name]",
    lines: ["[Address Line 1]", "[Address Line 2]", "[City], [Postcode]", "United Kingdom"],
    companiesHouseNumber: "[To be inserted post-incorporation]",
  },
  helpfulLinks: [
    // Next 16 typed-routes is strict: literal route strings need to be
    // cast through `as Route` for paths that the .next/types/ generator
    // hasn't materialised yet (legal pages added 2026-05-17). Mirrors
    // the existing pattern at app/(shop)/page.tsx:245.
    {
      label: "Terms & Conditions",
      url: "/terms" as Route,
    },
    {
      label: "Privacy Policy",
      url: "/privacy" as Route,
    },
    {
      label: "Cookie Policy",
      url: "/cookie-policy" as Route,
    },
    {
      label: "Delivery Information",
      url: "/delivery-policy" as Route,
    },
  ] satisfies ContactHelpfulLinkContent[],
  brandLine: "Wmart - Wholesale, simplified.",
} as const;